// The Revise room's typed writes, as DocumentStore makes them (QuoteMarks.swift `fixQuotes`, TextCleanup.swift
// `cleanUpText`, DocumentStore+Revisions.swift, ProjectEditing.swift `addAgentComments`, and the app's `fixFinding` and
// "Add to margin"): one Automerge change per document touched, with the Swift's checkpoint names and commit messages.
// Text is written as the Swift's `setText` writes it — a minimal diff (`updateText`) — so comment anchors outside what
// changed hold; quotation marks change scalar for scalar, so only the marks themselves are touched.
//
// Fragments open in an editor must not be rewritten here (the app's `rewritingText` rule): Reject of one change returns
// the replacement for the editor to apply undoably (`rejectReplacement`), and Reject All leaves open fragments out and
// returns their bases (`openBases`).
import * as A from '@automerge/automerge';
import { ROOT, SwiftReader } from '../automerge/swiftDoc.ts';
import { scalarLength, sliceScalars, utf16OffsetOfScalar } from '../automerge/offsets.ts';
import { fragmentDocName, type EditOptions } from '../edit/editProject.ts';
import { ModelError, StoreEditor, type AgentComment } from '../edit/storeEditor.ts';
import { bool, str, timestamp, type Writer } from '../edit/typed.ts';
import { MARKDOWN_KINDS } from '../model/manuscriptSelection.ts';
import type { FragmentKind, InkColor } from '../contract.ts';
import { putJSON } from '../settingsJSON.ts';
import { graphemes } from '../unicode.ts';
import {
  categoryNamed, commentCategory, COMMENT_CATEGORY_NAME_LIMIT, COMMENT_CATEGORY_PREFIX, decodeCommentCategory, nextCategoryInk,
  normalizedCategoryName, readCommentCategories, type CommentCategory,
} from './commentCategories.ts';
import { continuityComment, CONTINUITY_AUTHOR, STYLE_LINTER_AUTHOR, marginCommentFor, styleComment, type ReviseFinding } from './findings.ts';
import { checkQuotes, fixedQuotes } from '../text/quoteMarks.ts';
import {
  acceptPatch, changeFits, emptyRevisions, fragmentRevisionChanges, isRevisionActive, patchApplies, readRevisions, revisionsJSON, revisionTexts,
  storedText, type Revision, type RevisionChange, type Revisions, type RevisionTexts,
} from './revisions.ts';
import type { BuiltInStyleCheck, StyleFinding } from './style/linter.ts';
import { dashStyleName, type DashStyle } from './dashStyle.ts';
import { DIALOGUE_QUOTES_KEY, NUMBERS_KEY, type NumberStyle } from './styleSheet.ts';
import { CHOICE_PREFIX, choiceID, choiceJSON, type VariantChoice } from './variants.ts';
import type { QuoteStyle } from '../text/dialogueBalance.ts';
import { checkText, fixableCount, fixedText, type CleanupOptions } from './textCleanup.ts';
import { linkCommentCandidates, linkedComments } from './linkComments.ts';

/** `PassageFix`: a fix the Revise room offers for a passage — the words as they were found (Unicode scalars), and what
 *  goes in their place. */
export interface PassageFix { fragmentID: string; range: [number, number]; quote: string; replacement: string }
import { readFragment } from '../model/documents.ts';

/** `setText` inside a change: the text object (made if missing), updated by a minimal diff so anchors hold. */
function setText(d: Record<string, any>, text: string) {
  if (typeof d.text !== 'string') d.text = '';
  if (d.text === text) return;
  A.updateText(d as A.Doc<unknown>, ['text'], text);
}

const trimWhitespaceAndNewlines = (s: string) => s.replace(/^[\p{White_Space}]+|[\p{White_Space}]+$/gu, '');

export class ReviseEditor extends StoreEditor {
  /** Every document name this editor changed, in the order first changed. */
  readonly touched = new Set<string>();

  constructor(docs: Map<string, A.Doc<unknown>>, context: EditOptions) {
    super(docs, context);
  }

  protected override change(name: string, message: string | undefined, at: Date, fn: (w: Writer, r: SwiftReader, d: Record<string, any>) => void): boolean {
    const changed = super.change(name, message, at, fn);
    if (changed) this.touched.add(name);
    return changed;
  }

  /** The documents changed since the last call (to save and sync). */
  takeChanged(): string[] {
    const out = [...this.touched];
    this.touched.clear();
    return out;
  }

  /** The kind and text of a fragment here and stored as the dialect (screenplays and fragments still syncing: null). */
  private markdownText(id: string): string | null {
    const doc = this.docs.get(fragmentDocName(id));
    if (!doc) return null;
    const r = new SwiftReader(doc);
    if (!MARKDOWN_KINDS.includes((r.string(ROOT, 'kind') ?? '') as FragmentKind)) return null;
    return r.string(ROOT, 'text') ?? '';
  }

  /** Rewrites one fragment's text with `modifiedAt`, committed with `message` (the Swift's setText + setModifiedAt). */
  private rewrite(id: string, text: string, message: string, at: Date): boolean {
    // A locked document keeps its text (as the Mac's Replace, Fix Quotes, Clean Up and Restore pass it by).
    if (this.locks().has(id)) return false;
    return this.change(fragmentDocName(id), message, at, (w, _r, d) => {
      setText(d, text);
      w.putIfChanged([], 'modifiedAt', timestamp(at));
    });
  }

  // MARK: Quotation marks and common mistakes

  /** `fixQuotes(in:projectID:)`: fixes every fixable quotation mark in these fragments after a "Before fixing quotes"
   *  checkpoint. Screenplays and fragments not here yet are skipped. Returns how many marks changed. */
  fixQuotes(fragmentIDs: readonly string[]): number {
    const pending: { id: string; text: string; changes: number }[] = [];
    for (const id of fragmentIDs) {
      const text = this.markdownText(id);
      if (text === null) continue;
      const changes = checkQuotes(text).fixable;
      if (changes > 0) pending.push({ id, text: fixedQuotes(text), changes });
    }
    if (!pending.length) return 0;
    const at = this.now();
    this.createCheckpoint('Before fixing quotes', 'manual');
    for (const p of pending) this.rewrite(p.id, p.text, 'Fix quotation marks', at);
    return pending.reduce((n, p) => n + p.changes, 0);
  }

  /** `cleanUpText(in:projectID:options:)`: fixes the common mistakes the options look for, after a "Before cleaning up
   *  text" checkpoint; the text is diffed in. Returns how many places changed. */
  cleanUpText(fragmentIDs: readonly string[], options: CleanupOptions = {}): number {
    const pending: { id: string; text: string; changes: number }[] = [];
    for (const id of fragmentIDs) {
      const text = this.markdownText(id);
      if (text === null) continue;
      const changes = fixableCount(checkText(text, options));
      if (changes > 0) pending.push({ id, text: fixedText(text, options), changes });
    }
    if (!pending.length) return 0;
    const at = this.now();
    this.createCheckpoint('Before cleaning up text', 'manual');
    for (const p of pending) this.rewrite(p.id, p.text, 'Clean up common mistakes', at);
    return pending.reduce((n, p) => n + p.changes, 0);
  }

  // MARK: Link comments (LinkComments.swift)

  /** The link comments of each prose fragment here that would become links, with the text they'd make. */
  private linkCommentChanges(fragmentIDs: Iterable<string>) {
    const out: { id: string; original: string; edits: { at: number; value: string }[]; done: string[] }[] = [];
    for (const id of fragmentIDs) {
      const doc = this.docs.get(fragmentDocName(id));
      if (!doc) continue;
      const fragment = readFragment(doc);
      if (!MARKDOWN_KINDS.includes(fragment.kind)) continue;
      const candidates = linkCommentCandidates(doc, fragment.comments);
      if (!candidates.length) continue;
      const { linked, done } = linkedComments(fragment.text, candidates);
      if (done.length) out.push({ id, original: fragment.text, edits: linked.edits, done: done.map((c) => c.id) });
    }
    return out;
  }

  /** The project's fragment ids, sorted (`fragmentEntries().keys.sorted()`). */
  private fragmentIDs(): string[] {
    return [...this.project().fragments.keys()].sort();
  }

  /** `DocumentStore.linkCommentCount(in:)`: how many of the project's link comments would become links. */
  linkCommentCount(): number {
    return this.linkCommentChanges(this.fragmentIDs()).reduce((n, c) => n + c.done.length, 0);
  }

  /** `DocumentStore.convertLinkComments(in:)`: turns the project's link comments into links (locked documents passed
   *  by), after a "Before turning link comments into links" checkpoint, and resolves them. Only the inserted brackets
   *  change, so other comments' anchors hold. Returns how many. */
  convertLinkComments(): number {
    const locks = this.locks();
    const pending = this.linkCommentChanges(this.fragmentIDs().filter((id) => !locks.has(id)));
    if (!pending.length) return 0;
    const at = this.now();
    this.createCheckpoint('Before turning link comments into links', 'manual');
    for (const p of pending) {
      this.change(fragmentDocName(p.id), 'Turned link comments into links', at, (w, _r, d) => {
        // Only the brackets go in (never the text through a diff, which can split them round the words), so a
        // conversion another device makes before syncing leaves whole links for the Mac's mend to find.
        for (const e of p.edits) A.splice(d as A.Doc<unknown>, ['text'], utf16OffsetOfScalar(p.original, e.at), 0, e.value);
        for (const id of p.done) w.putIfChanged(['comments', id], 'status', str('resolved'));
        w.putIfChanged([], 'modifiedAt', timestamp(at));
      });
    }
    return pending.reduce((n, p) => n + p.done.length, 0);
  }

  /** The app's `fixFinding` for a fragment not open in the editor: the finding's passage becomes its replacement, if the
   *  text there still reads what was found. Returns whether it changed. */
  fixFinding(finding: ReviseFinding): boolean {
    if (finding.replacement === undefined) return false;
    const doc = this.docs.get(fragmentDocName(finding.fragmentID));
    if (!doc) return false;
    const text = storedText(doc);
    const [lo, hi] = finding.range;
    if (hi > scalarLength(text) || sliceScalars(text, lo, hi) !== finding.quote) return false;
    const next = text.slice(0, utf16OffsetOfScalar(text, lo)) + finding.replacement + text.slice(utf16OffsetOfScalar(text, hi));
    return this.rewrite(finding.fragmentID, next, `Fix ${finding.checkName.toLowerCase()}`, this.now());
  }

  /** `DocumentStore.applyFixes`: these fixes, after recording a checkpoint (`checkpointName`), so the whole change can be
   *  undone — exactly the passages the writer was shown, so one they chose to leave stays as it is. A fix whose words have
   *  moved or changed since, or that overlaps one after it, is passed by (the next check finds it again); locked documents,
   *  screenplays and fragments not here yet are skipped. The text is diffed in, so comment anchors hold. Returns how many
   *  were made. */
  applyFixes(fixes: readonly PassageFix[], checkpointName: string): number {
    const byFragment = new Map<string, PassageFix[]>();
    for (const f of fixes) byFragment.set(f.fragmentID, [...(byFragment.get(f.fragmentID) ?? []), f]);
    const locks = this.locks();
    const pending: { id: string; text: string; made: number }[] = [];
    for (const id of [...byFragment.keys()].sort()) {
      if (locks.has(id)) continue;
      const text = this.markdownText(id);
      if (text === null) continue;
      const scalars = [...text];
      let made = 0;
      let end = Infinity;
      for (const fix of [...byFragment.get(id)!].sort((a, b) => b.range[0] - a.range[0])) {
        const [lo, hi] = fix.range;
        if (hi > end || hi > scalars.length || scalars.slice(lo, hi).join('') !== fix.quote) continue;
        scalars.splice(lo, hi - lo, ...fix.replacement);
        end = lo;
        made++;
      }
      if (made > 0) pending.push({ id, text: scalars.join(''), made });
    }
    if (!pending.length) return 0;
    const at = this.now();
    this.createCheckpoint(checkpointName, 'manual');
    for (const p of pending) this.rewrite(p.id, p.text, checkpointName.replace('Before f', 'F'), at);
    return pending.reduce((n, p) => n + p.made, 0);
  }

  // MARK: Margin

  /** "Add to margin" for Revise findings: style findings as "Style linter" (`style-<id>`), the rest under their check's
   *  name. Once each: posting the same finding again adds nothing. Returns how many were added. */
  addFindingsToMargin(findings: readonly ReviseFinding[]): number {
    const byAuthor = new Map<string, AgentComment[]>();
    for (const f of findings) {
      const { author, comment } = marginCommentFor(f);
      byAuthor.set(author, [...(byAuthor.get(author) ?? []), comment]);
    }
    let added = 0;
    for (const [author, comments] of byAuthor) added += this.addAgentComments(comments, author);
    return added;
  }

  /** `addStyleToMargin`: comments from "Style linter" ("Check: message"), once each. */
  addStyleToMargin(findings: readonly StyleFinding[]): number {
    return this.addAgentComments(findings.map(styleComment), STYLE_LINTER_AUTHOR);
  }

  /** `addToMargin(_ findings: [ContinuityFinding])`: comments from "Continuity check", once each. */
  addContinuityToMargin(findings: readonly { id: string; fragmentID: string; range: [number, number]; message: string }[]): number {
    return this.addAgentComments(findings.map(continuityComment), CONTINUITY_AUTHOR);
  }

  // MARK: Style settings

  /** `ProjectDocument.setBuiltInStyleCheck` (the Revise column's switches): a boolean in `settings.style`, kept with the
   *  project (committed "Style check", as LibraryModel+Style does). Returns whether anything changed. */
  setBuiltInStyleCheck(check: BuiltInStyleCheck, enabled: boolean): boolean {
    return this.change('project', 'Style check', this.now(), (w) => {
      w.ensure(['settings', 'style']);
      w.putIfChanged(['settings', 'style'], check, bool(enabled));
    });
  }

  /** `ProjectDocument.setDashStyle` (Common mistakes' Dashes, kept with the project): the style, or null to follow the
   *  manuscript's usual one. Committed as LibraryModel+Cleanup commits it. Returns whether anything changed. */
  setDashStyle(style: DashStyle | null): boolean {
    const message = style ? `Dash style: ${dashStyleName(style)}` : 'Dash style: as the manuscript mostly has it';
    return this.change('project', message, this.now(), (w) => {
      w.ensure(['settings']);
      if (style) w.putIfChanged(['settings'], 'dashStyle', str(style));
      else w.delete(['settings'], 'dashStyle');
    });
  }

  /** `ProjectDocument.setDialogueQuotes` (the style sheet): the marks dialogue takes, or null to follow the manuscript.
   *  Returns whether anything changed. */
  setDialogueQuotes(style: QuoteStyle | null): boolean {
    const message = style ? `Style sheet: dialogue in ${style} quotes` : 'Style sheet: dialogue as the manuscript mostly has it';
    return this.change('project', message, this.now(), (w) => {
      w.ensure(['settings']);
      if (style) w.putIfChanged(['settings'], DIALOGUE_QUOTES_KEY, str(style));
      else w.delete(['settings'], DIALOGUE_QUOTES_KEY);
    });
  }

  /** `ProjectDocument.setNumberStyle` (the style sheet): how numbers are written in narration, or null for no preference.
   *  Returns whether anything changed. */
  setNumberStyle(style: NumberStyle | null): boolean {
    const message = style ? `Style sheet: numbers ${style === 'words' ? 'spelled out' : 'as digits'}` : 'Style sheet: numbers as they come';
    return this.change('project', message, this.now(), (w) => {
      w.ensure(['settings']);
      if (style) w.putIfChanged(['settings'], NUMBERS_KEY, str(style));
      else w.delete(['settings'], NUMBERS_KEY);
    });
  }

  /** `ProjectDocument.putStyleChoice` (the style sheet's Choices): a consistency choice as JSON at
   *  `styleSheet.choice.<id>`, committed as LibraryModel+Revise commits it ("Style sheet: grey, not gray"). */
  putStyleChoice(choice: VariantChoice): boolean {
    const message = choice.keep !== null
      ? `Style sheet: ${choice.keep}, not ${choice.forms.filter((f) => f.toLowerCase() !== choice.keep!.toLowerCase()).join(', ')}`
      : `Style sheet: ${choice.forms.join(' and ')} both`;
    return this.change('project', message, this.now(), (w) => {
      w.ensure(['settings']);
      w.putIfChanged(['settings'], CHOICE_PREFIX + choiceID(choice), str(choiceJSON(choice)));
    });
  }

  /** `ProjectDocument.deleteStyleChoice`: the set is raised again when the book has both forms. */
  deleteStyleChoice(choice: VariantChoice): boolean {
    return this.change('project', `Style sheet: no choice between ${choice.forms.join(' and ')}`, this.now(), (w, r) => {
      const key = CHOICE_PREFIX + choiceID(choice);
      const settings = r.map(ROOT, 'settings');
      if (settings && r.string(settings, key) !== undefined) w.delete(['settings'], key);
    });
  }

  // MARK: Comment categories

  /** `ProjectDocument.addCommentCategory`: a new category, in `ink` or the first ink no category has. Throws `ModelError`
   *  when the name is empty, too long, or another category's. */
  addCommentCategory(name: string, ink?: InkColor): CommentCategory {
    const existing = readCommentCategories(this.project());
    const category = commentCategory(name, ink ?? nextCategoryInk(existing));
    // The name's own id may belong to a category since renamed: comments in that one mustn't move to this.
    if (existing.some((c) => c.id === category.id)) category.id = this.id();
    this.saveCommentCategory(category);
    return category;
  }

  /** `ProjectDocument.putCommentCategory`: stores a category (new, renamed or recoloured), committed "Comment category:
   *  <name>". Throws `ModelError`, changing nothing, when the name is empty, too long or another category's, or when the
   *  stored entry can't be read here. Returns whether anything changed. */
  saveCommentCategory(category: CommentCategory): boolean {
    const name = normalizedCategoryName(category.name);
    if (name === '') throw new ModelError('invalid', 'A comment category needs a name.');
    if (graphemes(name).length > COMMENT_CATEGORY_NAME_LIMIT) {
      throw new ModelError('invalid', `A comment category’s name can be up to ${COMMENT_CATEGORY_NAME_LIMIT} characters.`);
    }
    const other = categoryNamed(name, readCommentCategories(this.project()));
    if (other && other.id !== category.id) throw new ModelError('invalid', `There’s already a comment category called “${other.name}”.`);
    return this.change('project', `Comment category: ${name}`, this.now(), (w) => {
      w.ensure(['settings']);
      putJSON(w, ['settings'], COMMENT_CATEGORY_PREFIX + category.id, { ...category, name }, decodeCommentCategory, `comment category “${name}”`);
    });
  }

  /** Renames or recolours the category stored under `id` as it is now — so a rename straight after a recolour (before the
   *  view has the recoloured category) doesn't put the old colour back. Returns whether anything changed; false when
   *  the category has gone. Throws as `saveCommentCategory` does. */
  updateCommentCategory(id: string, patch: { name?: string; color?: string }): boolean {
    const current = readCommentCategories(this.project()).find((c) => c.id === id);
    return current ? this.saveCommentCategory({ ...current, ...patch }) : false;
  }

  /** `ProjectDocument.deleteCommentCategory`, committed "Comment category: remove <name>". Comments in it are left as
   *  they are and read as uncategorised; adding a category of the same name again takes them back. */
  deleteCommentCategory(category: Pick<CommentCategory, 'id' | 'name'>): boolean {
    return this.change('project', `Comment category: remove ${category.name}`, this.now(), (w, r) => {
      const settings = r.map(ROOT, 'settings');
      if (settings && r.get(settings, COMMENT_CATEGORY_PREFIX + category.id)) w.delete(['settings'], COMMENT_CATEGORY_PREFIX + category.id);
    });
  }

  // MARK: Revisions

  private writeRevisions(value: Revisions, message: string) {
    const project = this.project();
    const json = revisionsJSON(project.settings.revisions, value); // throws, changing nothing, when unreadable
    this.change('project', message, this.now(), (w) => {
      w.ensure(['settings']);
      w.putIfChanged(['settings'], 'revisions', str(json));
    });
  }

  /** `startRevision(named:in:from:)`: records a "Start of …" checkpoint (or uses an existing one as the text before the
   *  revision) and adds the revision. The first revision sets the base. */
  startRevision(name: string, fromCheckpointID: string | null = null): Revision {
    const trimmed = trimWhitespaceAndNewlines(name);
    if (!trimmed) throw new ModelError('invalid', 'A revision needs a name.');
    let checkpoint: string;
    if (fromCheckpointID !== null) {
      if (!this.project().checkpoints.some((c) => c.id === fromCheckpointID)) throw new ModelError('notFound', `Not found: checkpoint ${fromCheckpointID}`);
      checkpoint = fromCheckpointID;
    } else {
      checkpoint = this.createCheckpoint(`Start of ${trimmed}`, 'manual').id;
    }
    const revisions = readRevisions(this.project());
    const revision: Revision = { id: this.id(), name: trimmed, checkpointID: checkpoint, started: this.now() };
    this.writeRevisions({ ...revisions, revisions: [...revisions.revisions, revision] }, `Start revision “${trimmed}”`);
    return revision;
  }

  /** `revisionTexts(of:in:)`. */
  revisionTexts(fragmentID: string): RevisionTexts | null {
    return revisionTexts(this.docs, fragmentID);
  }

  /** `revisionChanges(of:in:)` (`current`: the editor's text, else the stored one). */
  revisionChanges(fragmentID: string, current?: string): RevisionChange[] | null {
    return fragmentRevisionChanges(this.docs, fragmentID, current);
  }

  /** `acceptRevisionChanges`: the base takes the changes, so they're no longer marked; the text isn't touched. A change
   *  that no longer fits the base (already accepted, or the base moved on) is skipped. Returns how many were accepted. */
  acceptRevisionChanges(changes: readonly RevisionChange[], fragmentID: string, current?: string): number {
    if (!changes.length) return 0;
    const project = this.project();
    const revisions = readRevisions(project);
    const texts = revisionTexts(this.docs, fragmentID, project);
    if (!isRevisionActive(revisions) || !texts) return 0;
    const now = current ?? storedText(this.fragmentDoc(fragmentID));
    let base = texts.base;
    // Later changes first, so each patch's offsets still hold in the base as the patches before it left it.
    const patches = [...(revisions.accepted[fragmentID] ?? [])];
    let accepted = 0;
    for (const change of [...changes].sort((a, b) => b.base[0] - a.base[0])) {
      const patch = acceptPatch(change);
      if (!patchApplies(patch, base) || !changeFits(change, base, now)) continue;
      base = base.slice(0, change.base[0]) + change.inserted + base.slice(change.base[1]);
      patches.push(patch);
      accepted++;
    }
    if (!accepted) return 0;
    this.writeRevisions({ ...revisions, accepted: { ...revisions.accepted, [fragmentID]: patches } },
      accepted === 1 ? 'Accept a change' : `Accept ${accepted} changes`);
    return accepted;
  }

  /** `rejectRevisionChanges`: for a fragment NOT open in the editor, the base's words go back in their place. (An open
   *  fragment takes `rejectReplacement` through the editor instead, so Undo works.) */
  rejectRevisionChanges(changes: readonly RevisionChange[], fragmentID: string, base: string): void {
    if (!changes.length) return;
    let text = storedText(this.fragmentDoc(fragmentID));
    for (const change of [...changes].sort((a, b) => b.current[0] - a.current[0])) {
      if (change.current[1] > text.length || change.base[1] > base.length) continue;
      text = text.slice(0, change.current[0]) + base.slice(change.base[0], change.base[1]) + text.slice(change.current[1]);
    }
    this.rewrite(fragmentID, text, changes.length === 1 ? 'Reject a change' : `Reject ${changes.length} changes`, this.now());
  }

  /** `finishRevisions`: Accept All — the text stays, the marks go, an "Accepted revisions (…)" checkpoint records the
   *  moment, and revision mode ends. */
  finishRevisions(): void {
    const revisions = readRevisions(this.project());
    if (!isRevisionActive(revisions)) return;
    const names = revisions.revisions.map((r) => r.name).join(', ');
    this.createCheckpoint(`Accepted revisions (${names})`, 'manual');
    this.writeRevisions(emptyRevisions(), 'Accept all revisions');
  }

  /** `rejectAllRevisions(in:fragments:excluding:)`: every change in these fragments goes back to its base, after a
   *  "Before rejecting revisions" checkpoint (recorded if anything is to change, open fragments included). Revision mode
   *  stays on. Open fragments aren't rewritten: their bases come back for the editor to apply. */
  rejectAllRevisions(fragmentIDs: readonly string[], open: ReadonlySet<string> = new Set()): { changed: number; openBases: Record<string, string> } {
    const project = this.project();
    const pending: { id: string; base: string }[] = [];
    const openBases: Record<string, string> = {};
    for (const id of fragmentIDs) {
      const texts = revisionTexts(this.docs, id, project);
      const doc = this.docs.get(fragmentDocName(id));
      if (!texts || !doc || storedText(doc) === texts.base) continue;
      if (open.has(id)) openBases[id] = texts.base; else pending.push({ id, base: texts.base });
    }
    if (!pending.length && !Object.keys(openBases).length) return { changed: 0, openBases: {} };
    const at = this.now();
    this.createCheckpoint('Before rejecting revisions', 'manual');
    for (const p of pending) this.rewrite(p.id, p.base, 'Reject all revisions', at);
    return { changed: pending.length, openBases };
  }
}

export type { Revisions };
